import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { serializer } from './serializer';
import { serializer as restaurantSerializer } from '@features/restaurants/serializer';
import { getRouteDistance } from '@features/map/utils/distanceUtils.js';

// Helper function to prevent memory leaks - uses bus.on() pattern
function on(event, callback) {
  const handler = (...args) => {
    // bus.off(event, handler);
    callback(...args);
  };
  bus.on(event, handler);
}

// Helper pour finir le loading avec délai
function finishLoading() {
  setTimeout(() => {
    bus.emit(constants.EVENTS.LOADING.FINISH);
  }, 300);
}

// Helper pour démarrer le loading avec délai (éviter glitches)
async function startLoadingSmoothly() {
  bus.emit(constants.EVENTS.LOADING.START);
  await new Promise((resolve) => setTimeout(resolve, 150));
}

// ============ AUTO-LOAD CART ON APP READY ============
bus.on(constants.EVENTS.APP.READY, () => {
  // Only load cart if user has a userId
  const userId = serializer.getUserId();
  if (userId) {
    console.log('[Cart] App ready, loading cart for user:', userId);
    bus.emit(constants.EVENTS.CART.LOAD);
  }
});

// ============ LOAD CART ============
bus.on(constants.EVENTS.CART.LOAD, async () => {
  const requestId = `loadCart-${Date.now()}`;

  bus.emit(constants.EVENTS.CART.LOADING);
  await startLoadingSmoothly();

  // Get userId for the request
  const userId = serializer.getUserId();

  // Build URL with userId
  const url = constants.ENDPOINTS.CART.GET.replace(
    '{userId}',
    encodeURIComponent(userId)
  );

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: url,
    method: 'GET',
    data: {}
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = serializer.deserialize(res);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.LOADED, parsed.data);
      } else {
        bus.emit(constants.EVENTS.CART.LOAD_ERROR, parsed);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur de chargement',
          body: 'Impossible de charger votre panier. Veuillez réessayer.'
        });
      }
      finishLoading();
    }
  });
});

// ============ LOAD TEMP CART ============
bus.on(constants.EVENTS.CART.TEMP_CART_LOADING, async (payload) => {
  // 100% Local Temp Cart
  bus.emit(constants.EVENTS.CART.TEMP_CART_LOADED, {
    success: true,
    item: null
  });
});

// ============ UPDATE TEMP CART ============
bus.on(constants.EVENTS.CART.TEMP_CART_UPDATED, (payload) => {
  // 100% Local Temp Cart
  bus.emit(constants.EVENTS.CART.TEMP_CART_LOADED, {
    success: true,
    item: payload.itemData
  });
});

// ============ ADD ITEM ============
bus.on(constants.EVENTS.CART.ITEMS.ADD, async (payload) => {
  console.log('[Cart] ADD payload received:', payload);

  if (!window.$stores?.user?.state?.isLoggedIn) {
    window.$router.push('/login');
    return;
  }

  // Validation
  if (!payload?.dishId || !payload?.restaurantId) {
    bus.emit(constants.EVENTS.CART.ITEMS.ADD_ERROR, {
      message: 'Dish ID or Restaurant ID is required'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: "Impossible d'ajouter ce plat. Veuillez réessayer."
    });
    return;
  }

  const restaurant = await getRestaurant(payload.restaurantId);
  console.log('[Cart Controller] Restaurant fetched:', restaurant);
  if (!restaurant?.id) {
    bus.emit(constants.EVENTS.CART.ITEMS.ADD_ERROR, {
      message: 'Restaurant not found'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: "Impossible d'ajouter ce plat. Veuillez réessayer."
    });
    return;
  }

  if (!restaurant?.isWithinOpeningHours) {
    bus.emit(constants.EVENTS.CART.ITEMS.ADD_ERROR, {
      message: 'Restaurant not within opening hours'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: "En dehors des heures d'ouverture",
      body: "Ce restaurant n'accepte pas de commandes en ce moment. Consultez ses horaires d'ouverture."
    });
    return;
  }

  if (!restaurant?.isOpen) {
    bus.emit(constants.EVENTS.CART.ITEMS.ADD_ERROR, {
      message: 'Restaurant is closed'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Restaurant fermé',
      body: 'Ce restaurant est fermé. Veuillez réessayer plus tard.'
    });
    return;
  }
  const requestId = `addItem-${Date.now()}`;

  bus.emit(constants.EVENTS.CART.ITEMS.ADDING);
  await startLoadingSmoothly();

  const requestData = serializer.serialize(payload);
  console.log('[Cart] Serialized requestData:', requestData);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.CART.ITEMS.ADD,
    method: 'POST',
    data: requestData
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = serializer.deserialize(res);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.ITEMS.ADDED, parsed.data);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'success',
          title: 'Ajouté au panier',
          body: 'Ce plat a été ajouté à votre panier.'
        });
      } else {
        bus.emit(constants.EVENTS.CART.ITEMS.ADD_ERROR, parsed);

        // Message utilisateur selon l'erreur
        const errorCode = parsed.error?.code || parsed.data?.code;
        if (errorCode === 'DIFFERENT_RESTAURANT') {
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Restaurant différent',
            body: "Vous avez déjà des articles d'un autre restaurant dans votre panier. Veuillez d'abord terminer votre commande actuelle ou vider votre panier."
          });
        } else if (errorCode === 'MISSING_RESTAURANT_ID') {
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: "Impossible d'ajouter ce plat. Restaurant non trouvé."
          });
        } else {
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: "Impossible d'ajouter ce plat. Veuillez réessayer."
          });
        }
      }
      finishLoading();
    }
  });
});

// ============ UPDATE ITEM ============
bus.on(constants.EVENTS.CART.ITEMS.UPDATE, async (payload) => {
  if (!payload?.itemId) {
    bus.emit(constants.EVENTS.CART.ITEMS.UPDATE_ERROR, {
      message: 'Item ID is required'
    });
    return;
  }

  const requestId = `updateItem-${Date.now()}`;

  bus.emit(constants.EVENTS.CART.ITEMS.UPDATING);
  await startLoadingSmoothly();

  const userId = serializer.getUserId();
  const url = constants.ENDPOINTS.CART.ITEMS.QUANTITY.replace(
    '{userId}',
    encodeURIComponent(userId)
  ).replace('{itemId}', payload.itemId);

  const requestData = serializer.serialize(payload);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: url,
    method: 'POST',
    data: requestData
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = serializer.deserialize(res);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.ITEMS.UPDATED, parsed.data);
      } else {
        bus.emit(constants.EVENTS.CART.ITEMS.UPDATE_ERROR, parsed);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur',
          body: 'Impossible de modifier la quantité. Veuillez réessayer.'
        });
      }
      finishLoading();
    }
  });
});

// ============ NEW - UPDATE ITEM OPTIONS ============
bus.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATE, async (payload) => {
  if (!payload?.itemId || !Array.isArray(payload?.options)) {
    bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATE_ERROR, {
      message: 'Invalid payload'
    });
    return;
  }

  const userId = serializer.getUserId();
  const store = window.$stores?.cart;
  const currentItem = store?.state?.cart?.items?.find(
    (i) => i.id === payload.itemId
  );

  if (!currentItem) {
    bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATE_ERROR, {
      message: 'Item not found in cart'
    });
    return;
  }

  bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATING);
  await startLoadingSmoothly();

  // Diff logic: compare payload.options with currentItem.options
  const requests = [];

  const currentOptionsMap = {};
  const currentCartOptionIds = {};
  (currentItem.options || []).forEach((opt) => {
    const combinedId = `${opt.optionId}---${opt.valueId}`;
    currentOptionsMap[combinedId] = opt.quantity;
    currentCartOptionIds[combinedId] = opt.cartOptionId || opt.id;
  });

  const desiredOptionsMap = {};
  payload.options.forEach((opt) => {
    const combinedId = `${opt.optionId}---${opt.valueId}`;
    desiredOptionsMap[combinedId] = opt.quantity || 1;
  });

  const allOptionIds = new Set([
    ...Object.keys(currentOptionsMap),
    ...Object.keys(desiredOptionsMap)
  ]);

  allOptionIds.forEach((optId) => {
    const currentQ = currentOptionsMap[optId] || 0;
    const desiredQ = desiredOptionsMap[optId] || 0;
    const diff = desiredQ - currentQ;

    if (currentQ === 0 && desiredQ > 0) {
      const optionData = payload.options.find(
        (o) => `${o.optionId}---${o.valueId}` === optId
      );
      requests.push({
        url: constants.ENDPOINTS.CART.ITEMS.OPTIONS.ADD.replace(
          '{userId}',
          encodeURIComponent(userId)
        ),
        method: 'POST',
        data: {
          userId,
          cartItemId: payload.itemId,
          option: {
            id: optId, // This is the combined ID
            name: `${optionData?.optionName} • ${optionData?.valueName}`,
            price: parseInt(optionData?.price || 0, 10),
            quantity: desiredQ
          }
        }
      });
    } else if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        requests.push({
          url: constants.ENDPOINTS.CART.ITEMS.OPTIONS.INCREASE.replace(
            '{userId}',
            encodeURIComponent(userId)
          )
            .replace('{itemId}', payload.itemId)
            .replace('{optionId}', currentCartOptionIds[optId] || optId),
          method: 'POST'
        });
      }
    } else if (diff < 0) {
      if (desiredQ === 0) {
        requests.push({
          url: constants.ENDPOINTS.CART.ITEMS.OPTIONS.REMOVE.replace(
            '{userId}',
            encodeURIComponent(userId)
          )
            .replace('{itemId}', payload.itemId)
            .replace('{optionId}', currentCartOptionIds[optId] || optId),
          method: 'POST'
        });
      } else {
        for (let i = 0; i < Math.abs(diff); i++) {
          requests.push({
            url: constants.ENDPOINTS.CART.ITEMS.OPTIONS.DECREASE.replace(
              '{userId}',
              encodeURIComponent(userId)
            )
              .replace('{itemId}', payload.itemId)
              .replace('{optionId}', currentCartOptionIds[optId] || optId),
            method: 'POST'
          });
        }
      }
    }
  });

  if (requests.length === 0) {
    bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATED, store.state.cart);
    finishLoading();
    return;
  }

  let lastParsed = null;
  let hasError = false;

  for (const req of requests) {
    const requestId = `optDiff-${Date.now()}-${Math.random()}`;
    const reqPromise = new Promise((resolve) => {
      const handler = (res) => {
        if (res.id === requestId) {
          bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
          resolve(res);
        }
      };
      bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
      bus.emit(constants.EVENTS.NETWORK.REQUEST, {
        id: requestId,
        url: req.url,
        method: req.method,
        data: req.data || {}
      });
    });

    const res = await reqPromise;
    const parsed = serializer.deserialize(res);
    if (!parsed.success) {
      hasError = true;
      lastParsed = parsed;
      break;
    }
    lastParsed = parsed;
  }

  if (!hasError && lastParsed) {
    bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATED, lastParsed.data);
  } else {
    bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATE_ERROR, lastParsed);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Erreur de synchronisation des options.'
    });
  }
  finishLoading();
});

// ============ NEW - UPDATE ITEM ACCOMPANIMENTS ============
bus.on(constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATE, async (payload) => {
  if (!payload?.itemId || !Array.isArray(payload?.accompaniments)) {
    bus.emit(constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATE_ERROR, {
      message: 'Invalid payload'
    });
    return;
  }

  const userId = serializer.getUserId();
  const store = window.$stores?.cart;
  const currentItem = store?.state?.cart?.items?.find(
    (i) => i.id === payload.itemId
  );

  if (!currentItem) {
    bus.emit(constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATE_ERROR, {
      message: 'Item not found in cart'
    });
    return;
  }

  bus.emit(constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATING);
  await startLoadingSmoothly();

  const requests = [];
  const currentAccsMap = {};
  const currentCartAccIds = {};
  (currentItem.accompaniments || []).forEach((acc) => {
    currentAccsMap[acc.id] = acc.quantity;
    currentCartAccIds[acc.id] = acc.cartAccId || acc.id;
  });

  const desiredAccsMap = {};
  payload.accompaniments.forEach((acc) => {
    desiredAccsMap[acc.id] = acc.quantity || 1;
  });

  const allAccIds = new Set([
    ...Object.keys(currentAccsMap),
    ...Object.keys(desiredAccsMap)
  ]);

  allAccIds.forEach((accId) => {
    const currentQ = currentAccsMap[accId] || 0;
    const desiredQ = desiredAccsMap[accId] || 0;
    const diff = desiredQ - currentQ;

    if (currentQ === 0 && desiredQ > 0) {
      const accData = payload.accompaniments.find((a) => a.id === accId);
      requests.push({
        url: constants.ENDPOINTS.CART.ITEMS.ACCOMPANIMENTS.ADD.replace(
          '{userId}',
          encodeURIComponent(userId)
        ),
        method: 'POST',
        data: {
          userId,
          cartItemId: payload.itemId,
          accompaniment: {
            id: accId,
            name: accData?.name || '',
            price: parseInt(accData?.price || 0, 10),
            quantity: desiredQ
          }
        }
      });
    } else if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        requests.push({
          url: constants.ENDPOINTS.CART.ITEMS.ACCOMPANIMENTS.INCREASE.replace(
            '{userId}',
            encodeURIComponent(userId)
          )
            .replace('{itemId}', payload.itemId)
            .replace('{accId}', currentCartAccIds[accId] || accId),
          method: 'POST'
        });
      }
    } else if (diff < 0) {
      if (desiredQ === 0) {
        requests.push({
          url: constants.ENDPOINTS.CART.ITEMS.ACCOMPANIMENTS.REMOVE.replace(
            '{userId}',
            encodeURIComponent(userId)
          )
            .replace('{itemId}', payload.itemId)
            .replace('{accId}', currentCartAccIds[accId] || accId),
          method: 'POST'
        });
      } else {
        for (let i = 0; i < Math.abs(diff); i++) {
          requests.push({
            url: constants.ENDPOINTS.CART.ITEMS.ACCOMPANIMENTS.DECREASE.replace(
              '{userId}',
              encodeURIComponent(userId)
            )
              .replace('{itemId}', payload.itemId)
              .replace('{accId}', currentCartAccIds[accId] || accId),
            method: 'POST'
          });
        }
      }
    }
  });

  if (requests.length === 0) {
    bus.emit(
      constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATED,
      store.state.cart
    );
    finishLoading();
    return;
  }

  let lastParsed = null;
  let hasError = false;

  for (const req of requests) {
    const requestId = `accDiff-${Date.now()}-${Math.random()}`;
    const reqPromise = new Promise((resolve) => {
      const handler = (res) => {
        if (res.id === requestId) {
          bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
          resolve(res);
        }
      };
      bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
      bus.emit(constants.EVENTS.NETWORK.REQUEST, {
        id: requestId,
        url: req.url,
        method: req.method,
        data: req.data || {}
      });
    });

    const res = await reqPromise;
    const parsed = serializer.deserialize(res);
    if (!parsed.success) {
      hasError = true;
      lastParsed = parsed;
      break;
    }
    lastParsed = parsed;
  }

  if (!hasError && lastParsed) {
    bus.emit(
      constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATED,
      lastParsed.data
    );
  } else {
    bus.emit(
      constants.EVENTS.CART.ITEMS.ACCOMPANIMENTS.UPDATE_ERROR,
      lastParsed
    );
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Erreur de synchronisation des accompagnements.'
    });
  }
  finishLoading();
});

// ============ NEW - UPDATE ITEM DRINKS ============
bus.on(constants.EVENTS.CART.ITEMS.DRINKS.UPDATE, async (payload) => {
  if (!payload?.itemId || !Array.isArray(payload?.drinks)) {
    bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATE_ERROR, {
      message: 'Invalid payload'
    });
    return;
  }

  const userId = serializer.getUserId();
  const store = window.$stores?.cart;
  const currentItem = store?.state?.cart?.items?.find(
    (i) => i.id === payload.itemId
  );

  if (!currentItem) {
    bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATE_ERROR, {
      message: 'Item not found in cart'
    });
    return;
  }

  bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATING);
  await startLoadingSmoothly();

  const requests = [];
  const currentDrinksMap = {};
  const currentCartDrinkIds = {};
  (currentItem.drinks || []).forEach((d) => {
    currentDrinksMap[d.drinkId] = d.quantity;
    currentCartDrinkIds[d.drinkId] = d.cartDrinkId || d.id;
  });

  const desiredDrinksMap = {};
  payload.drinks.forEach((d) => {
    desiredDrinksMap[d.drinkId || d.id] = d.quantity || 1;
  });

  const allDrinkIds = new Set([
    ...Object.keys(currentDrinksMap),
    ...Object.keys(desiredDrinksMap)
  ]);

  allDrinkIds.forEach((drinkId) => {
    const currentQ = currentDrinksMap[drinkId] || 0;
    const desiredQ = desiredDrinksMap[drinkId] || 0;
    const diff = desiredQ - currentQ;

    if (currentQ === 0 && desiredQ > 0) {
      const drinkData = payload.drinks.find(
        (d) => (d.drinkId || d.id) === drinkId
      );
      requests.push({
        url: constants.ENDPOINTS.CART.DRINKS.ADD.replace(
          '{userId}',
          encodeURIComponent(userId)
        ),
        method: 'POST',
        data: {
          userId,
          drink: {
            id: drinkId,
            name: drinkData?.name || '',
            price: parseInt(drinkData?.price || 0, 10)
          },
          quantity: desiredQ
        }
      });
    } else if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        requests.push({
          url: constants.ENDPOINTS.CART.ITEMS.DRINKS.INCREASE.replace(
            '{userId}',
            encodeURIComponent(userId)
          ).replace('{drinkId}', currentCartDrinkIds[drinkId] || drinkId), // API expects drinkId
          method: 'POST'
        });
      }
    } else if (diff < 0) {
      if (desiredQ === 0) {
        requests.push({
          url: constants.ENDPOINTS.CART.ITEMS.DRINKS.REMOVE.replace(
            '{userId}',
            encodeURIComponent(userId)
          ).replace('{drinkId}', currentCartDrinkIds[drinkId] || drinkId),
          method: 'POST'
        });
      } else {
        for (let i = 0; i < Math.abs(diff); i++) {
          requests.push({
            url: constants.ENDPOINTS.CART.ITEMS.DRINKS.DECREASE.replace(
              '{userId}',
              encodeURIComponent(userId)
            ).replace('{drinkId}', currentCartDrinkIds[drinkId] || drinkId),
            method: 'POST'
          });
        }
      }
    }
  });

  if (requests.length === 0) {
    bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATED, store.state.cart);
    finishLoading();
    return;
  }

  let lastParsed = null;
  let hasError = false;

  for (const req of requests) {
    const requestId = `drinkDiff-${Date.now()}-${Math.random()}`;
    const reqPromise = new Promise((resolve) => {
      const handler = (res) => {
        if (res.id === requestId) {
          bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
          resolve(res);
        }
      };
      bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
      bus.emit(constants.EVENTS.NETWORK.REQUEST, {
        id: requestId,
        url: req.url,
        method: req.method,
        data: req.data || {}
      });
    });

    const res = await reqPromise;
    const parsed = serializer.deserialize(res);
    if (!parsed.success) {
      hasError = true;
      lastParsed = parsed;
      break;
    }
    lastParsed = parsed;
  }

  if (!hasError && lastParsed) {
    bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATED, lastParsed.data);
  } else {
    bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATE_ERROR, lastParsed);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Erreur de synchronisation des boissons.'
    });
  }
  finishLoading();
});

// ============ NEW - UPDATE GLOBAL DRINKS ============
bus.on(constants.EVENTS.CART.DRINKS.UPDATE, async (payload) => {
  // Global drinks update will require diffing with cart.drinks
  if (!Array.isArray(payload?.drinks)) {
    bus.emit(constants.EVENTS.CART.DRINKS.UPDATE_ERROR, {
      message: 'Drinks must be an array'
    });
    return;
  }

  const userId = serializer.getUserId();
  const store = window.$stores?.cart;
  const currentDrinks = store?.state?.cart?.drinks || [];

  bus.emit(constants.EVENTS.CART.DRINKS.UPDATING);
  await startLoadingSmoothly();

  const requests = [];
  const currentDrinksMap = {};
  const currentCartDrinkIds = {};
  currentDrinks.forEach((d) => {
    currentDrinksMap[d.drinkId] = d.quantity;
    currentCartDrinkIds[d.drinkId] = d.cartDrinkId || d.id;
  });

  const desiredDrinksMap = {};
  payload.drinks.forEach((d) => {
    desiredDrinksMap[d.drinkId || d.id] = d.quantity || 1;
  });

  const allDrinkIds = new Set([
    ...Object.keys(currentDrinksMap),
    ...Object.keys(desiredDrinksMap)
  ]);

  allDrinkIds.forEach((drinkId) => {
    const currentQ = currentDrinksMap[drinkId] || 0;
    const desiredQ = desiredDrinksMap[drinkId] || 0;
    const diff = desiredQ - currentQ;

    if (currentQ === 0 && desiredQ > 0) {
      const drinkData = payload.drinks.find(
        (d) => (d.drinkId || d.id) === drinkId
      );
      requests.push({
        url: constants.ENDPOINTS.CART.DRINKS.ADD.replace(
          '{userId}',
          encodeURIComponent(userId)
        ),
        method: 'POST',
        data: {
          userId,
          drink: {
            id: drinkId,
            name: drinkData?.name || '',
            price: parseInt(drinkData?.price || 0, 10)
          },
          quantity: desiredQ
        }
      });
    } else if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        requests.push({
          url: constants.ENDPOINTS.CART.DRINKS.INCREASE.replace(
            '{userId}',
            encodeURIComponent(userId)
          ).replace('{drinkId}', currentCartDrinkIds[drinkId] || drinkId),
          method: 'POST'
        });
      }
    } else if (diff < 0) {
      if (desiredQ === 0) {
        requests.push({
          url: constants.ENDPOINTS.CART.DRINKS.REMOVE.replace(
            '{userId}',
            encodeURIComponent(userId)
          ).replace('{drinkId}', currentCartDrinkIds[drinkId] || drinkId),
          method: 'POST'
        });
      } else {
        for (let i = 0; i < Math.abs(diff); i++) {
          requests.push({
            url: constants.ENDPOINTS.CART.DRINKS.DECREASE.replace(
              '{userId}',
              encodeURIComponent(userId)
            ).replace('{drinkId}', currentCartDrinkIds[drinkId] || drinkId),
            method: 'POST'
          });
        }
      }
    }
  });

  if (requests.length === 0) {
    bus.emit(constants.EVENTS.CART.DRINKS.UPDATED, store.state.cart);
    finishLoading();
    return;
  }

  let lastParsed = null;
  let hasError = false;

  for (const req of requests) {
    const requestId = `globalDrinkDiff-${Date.now()}-${Math.random()}`;
    const reqPromise = new Promise((resolve) => {
      const handler = (res) => {
        if (res.id === requestId) {
          bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
          resolve(res);
        }
      };
      bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
      bus.emit(constants.EVENTS.NETWORK.REQUEST, {
        id: requestId,
        url: req.url,
        method: req.method,
        data: req.data || {}
      });
    });

    const res = await reqPromise;
    const parsed = serializer.deserialize(res);
    if (!parsed.success) {
      hasError = true;
      lastParsed = parsed;
      break;
    }
    lastParsed = parsed;
  }

  if (!hasError && lastParsed) {
    bus.emit(constants.EVENTS.CART.DRINKS.UPDATED, lastParsed.data);
  } else {
    bus.emit(constants.EVENTS.CART.DRINKS.UPDATE_ERROR, lastParsed);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Erreur de synchronisation.'
    });
  }
  finishLoading();
});

// ============ REMOVE ITEM ============
bus.on(constants.EVENTS.CART.ITEMS.REMOVE, async (payload) => {
  if (!payload?.itemId) {
    bus.emit(constants.EVENTS.CART.ITEMS.REMOVE_ERROR, {
      message: 'Item ID is required'
    });
    return;
  }

  const requestId = `removeItem-${Date.now()}`;

  bus.emit(constants.EVENTS.CART.ITEMS.REMOVING);
  await startLoadingSmoothly();

  const userId = serializer.getUserId();
  const url = constants.ENDPOINTS.CART.ITEMS.REMOVE.replace(
    '{userId}',
    encodeURIComponent(userId)
  ).replace('{itemId}', payload.itemId);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: url,
    method: 'POST',
    data: {}
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = serializer.deserialize(res);
      console.log('🚀 ~ parsed:', parsed);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.ITEMS.REMOVED, parsed.data);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'success',
          title: 'Retiré',
          body: 'Ce plat a été retiré de votre panier.'
        });
      } else {
        bus.emit(constants.EVENTS.CART.ITEMS.REMOVE_ERROR, parsed);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur',
          body: 'Impossible de retirer ce plat. Veuillez réessayer.'
        });
      }
      finishLoading();
    }
  });
});

// ============ CLEAR CART ============
bus.on(constants.EVENTS.CART.CLEAR, async () => {
  const requestId = `clearCart-${Date.now()}`;

  bus.emit(constants.EVENTS.CART.CLEARING);
  await startLoadingSmoothly();

  const userId = serializer.getUserId();
  const url = constants.ENDPOINTS.CART.CLEAR.replace(
    '{userId}',
    encodeURIComponent(userId)
  );

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: url,
    method: 'POST',
    data: {}
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = serializer.deserialize(res);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.CLEARED);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'success',
          title: 'Panier vidé',
          body: 'Votre panier a été vidé.'
        });
      } else {
        bus.emit(constants.EVENTS.CART.CLEAR_ERROR, parsed);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur',
          body: 'Impossible de vider le panier. Veuillez réessayer.'
        });
      }
      finishLoading();
    }
  });
});

// ============ CALCULATE FEES ============
bus.on(constants.EVENTS.CART.FEES_CALCULATE, async (payload) => {
  const requestId = `calculateFees-${Date.now()}`;

  console.log('[Cart Controller] FEES_CALCULATE received:', payload);

  // Validation
  if (!payload?.restaurantId) {
    console.error(
      '[Cart Controller] Missing restaurantId in FEES_CALCULATE payload'
    );
    bus.emit(constants.EVENTS.CART.FEES_CALCULATE_ERROR, {
      message: 'Restaurant ID is required',
      code: 'MISSING_RESTAURANT_ID'
    });
    return;
  }

  bus.emit(constants.EVENTS.CART.FEES_CALCULATING);

  // Build request data with userId and cartItems from store
  const store = window.$stores?.cart;
  const requestData = serializer.serializeFeesCalculation({
    ...payload,
    userId: serializer.getUserId(),
    cartItems: store.state.cart?.items || []
  });
  console.log('[Cart Controller] Serialized fees request:', requestData);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.CART.FEES_CALCULATE,
    method: 'POST',
    data: requestData
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      console.log('[Cart Controller] Fees calculation response:', res);
      const parsed = serializer.deserializeFeesCalculation(res);

      if (parsed.success) {
        console.log(
          '[Cart Controller] Fees calculated successfully:',
          parsed.data
        );
        bus.emit(constants.EVENTS.CART.FEES_CALCULATED, parsed.data);
      } else {
        console.error('[Cart Controller] Fees calculation error:', parsed);
        bus.emit(constants.EVENTS.CART.FEES_CALCULATE_ERROR, {
          message: parsed.message || 'Impossible de calculer les frais',
          code: parsed.code || 'FEES_CALCULATION_ERROR'
        });
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur de calcul',
          body:
            parsed.message || 'Impossible de calculer les frais de livraison'
        });
      }
    }
  });
});

// ============ CALCULATE ORDER FEES (WITH DISTANCE) ============
bus.on(constants.EVENTS.CART.ORDER_FEES_CALCULATE, async (payload) => {
  const requestId = `calculateOrderFees-${Date.now()}`;

  console.log('[Cart Controller] ORDER_FEES_CALCULATE received:', payload);

  // Validation
  if (!payload?.restaurantId) {
    console.error(
      '[Cart Controller] Missing restaurantId in ORDER_FEES_CALCULATE payload'
    );
    bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATE_ERROR, {
      message: 'Restaurant ID is required',
      code: 'MISSING_RESTAURANT_ID'
    });
    return;
  }

  // Check if this is a pickup order
  const isPickupOrder =
    payload.deliveryOption === 'manualyTook' || payload.isPickup === true;

  // For delivery orders, validate delivery address
  if (!isPickupOrder) {
    if (
      !payload?.deliveryAddress ||
      !payload.deliveryAddress.lat ||
      !payload.deliveryAddress.lng
    ) {
      console.error(
        '[Cart Controller] Missing deliveryAddress in ORDER_FEES_CALCULATE payload'
      );
      bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATE_ERROR, {
        message: 'Delivery address is required',
        code: 'MISSING_DELIVERY_ADDRESS'
      });
      return;
    }
  }

  bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATING);

  try {
    let feesRequestData;

    // For pickup orders, we don't need distance calculation
    if (isPickupOrder) {
      console.log(
        '[Cart Controller] Pickup order detected - skipping distance calculation'
      );

      const store = window.$stores?.cart;
      feesRequestData = {
        restaurantId: payload.restaurantId,
        items: store.state.cart?.items || [],
        deliveryOption: payload.deliveryOption || 'manualyTook',
        isPickup: true,
        couponCode: store.state.cart?.coupon?.code || null,
        userId: serializer.getUserId()
      };

      console.log(
        '[Cart Controller] Calling calculate-fees with pickup:',
        feesRequestData
      );
    } else {
      // Step 1: Fetch restaurant details to get its position
      const restaurant = await getRestaurant(payload.restaurantId);
      console.log('[Cart Controller] Restaurant fetched:', restaurant);

      // Extract restaurant position
      const restaurantPosition = {
        lat: restaurant.location?.coordinates?.[1] || restaurant.lat,
        lng: restaurant.location?.coordinates?.[0] || restaurant.lng
      };

      if (!restaurantPosition.lat || !restaurantPosition.lng) {
        throw new Error('Restaurant position not found');
      }

      console.log('[Cart Controller] Restaurant position:', restaurantPosition);

      // Step 2: Calculate route distance using Google Maps

      const routeResult = await getRouteDistance(restaurantPosition, {
        lat: payload.deliveryAddress.lat,
        lng: payload.deliveryAddress.lng
      });

      console.log('[Cart Controller] Route distance calculated:', routeResult);

      // Step 3: Calculate fees using the new endpoint with distance
      const store = window.$stores?.cart;
      feesRequestData = {
        restaurantId: payload.restaurantId,
        distanceKm: routeResult.distanceKm,
        durationMin: routeResult.durationMin,
        items: store.state.cart?.items || [],
        deliveryAddress: payload.deliveryAddress,
        deliveryOption: payload.deliveryOption || 'actualPosition',
        isPickup: false,
        couponCode: store.state.cart?.coupon?.code || null,
        userId: serializer.getUserId()
      };

      console.log(
        '[Cart Controller] Calling calculate-fees with delivery:',
        feesRequestData
      );
    }

    // Make the API call
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: constants.ENDPOINTS.CART.FEES_CALCULATE,
      method: 'POST',
      data: feesRequestData
    });

    on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
      if (res.id === requestId) {
        console.log('[Cart Controller] Order fees calculation response:', res);

        const parsed = serializer.deserializeFeesCalculation(res);

        if (parsed.success) {
          const feesData = parsed.data.fees || parsed.data;
          const cartData = parsed.data.cart;
          console.log(
            '[Cart Controller] Order fees calculated successfully:',
            feesData
          );

          // For pickup orders, format the response appropriately
          const responseData = {
            ...feesData,
            // Preserve distance/duration info if it was calculated
            distanceKm: isPickupOrder
              ? 0
              : feesData.distanceKm ||
                feesData.distance ||
                feesRequestData.distanceKm ||
                0,
            durationMin: isPickupOrder
              ? 15
              : feesData.durationMin || feesRequestData.durationMin || 0,
            distanceText: isPickupOrder
              ? '0 km'
              : feesData.distanceFormatted ||
                payload.distanceText ||
                `${feesRequestData.distanceKm || 0} km`,
            durationText: isPickupOrder
              ? '15 min'
              : feesData.durationFormatted ||
                payload.durationText ||
                `${feesRequestData.durationMin || 0} min`
          };

          bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATED, responseData);

          // If the server returned an updated cart, refresh the local state
          if (cartData) {
            const parsedCart = serializer.deserialize({
              data: { statusCode: 200, data: cartData }
            });
            if (parsedCart.success) {
              bus.emit(constants.EVENTS.CART.LOADED, parsedCart.data);
            }
          }
        } else {
          console.error('[Cart Controller] Order fees calculation error:', res);
          bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATE_ERROR, {
            message:
              res.data?.error?.message || 'Impossible de calculer les frais',
            code: res.data?.error?.code || 'ORDER_FEES_CALCULATION_ERROR'
          });
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur de calcul',
            body:
              res.data?.error?.message ||
              'Impossible de calculer les frais de livraison'
          });
        }
      }
    });
  } catch (error) {
    console.error('[Cart Controller] Error in calculateOrderFees:', error);
    bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATE_ERROR, {
      message: error.message || 'Erreur lors du calcul des frais',
      code: 'ORDER_FEES_CALCULATION_ERROR'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de calcul',
      body: error.message || 'Impossible de calculer les frais de livraison'
    });
  }
});

// ============ APPLY COUPON ============
bus.on(constants.EVENTS.CART.COUPON.APPLY, async (payload) => {
  console.log('🚀 ~ payload:', payload);
  if (!payload?.code) {
    bus.emit(constants.EVENTS.CART.COUPON.APPLY_ERROR, {
      message: 'Code promo requis'
    });
    return;
  }

  bus.emit(constants.EVENTS.CART.COUPON.APPLYING);
  await startLoadingSmoothly();

  const userId = serializer.getUserId();
  const store = window.$stores?.cart;

  // Calcul du montant total du panier pour la validation
  const cartItems = store?.state?.cart?.items || [];
  const cartAmount = cartItems.reduce((sum, item) => {
    return sum + (item.price || 0) * (item.quantity || 1);
  }, 0);

  // ── Étape 1 : Valider le coupon ──────────────────────────────────────────
  const validateRequestId = `validateCoupon-${Date.now()}`;

  const validateResult = await new Promise((resolve) => {
    const handler = (res) => {
      if (res.id === validateRequestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        resolve(res);
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: validateRequestId,
      url: constants.ENDPOINTS.CART.VALIDATE_COUPON,
      method: 'POST',
      data: { code: payload.code, amount: cartAmount }
    });
  });

  const validatedParsed = serializer.deserializeCouponValidate(
    validateResult.data
  );
  console.log('🚀 ~ validateResult:', validatedParsed);

  if (!validatedParsed.success) {
    const errorMsg =
      validatedParsed.error?.message ||
      validatedParsed.message ||
      'Coupon invalide ou expiré';
    const titleError =
      validatedParsed.error?.titleError ||
      validatedParsed.titleError ||
      'Coupon invalide ou expiré';
    bus.emit(constants.EVENTS.CART.COUPON.APPLY_ERROR, { message: errorMsg });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: titleError,
      body: errorMsg
    });
    finishLoading();
    return;
  }
  if (
    validatedParsed.data?.coupon?.metadata?.userId &&
    validatedParsed.data?.coupon?.metadata?.userId !== userId
  ) {
    bus.emit(constants.EVENTS.CART.COUPON.APPLY_ERROR, {
      message: 'Coupon invalide pour ce client'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Coupon invalide',
      body: 'Coupon invalide pour ce client'
    });
    finishLoading();
    return;
  }

  // Extraire discount et type depuis la réponse de validation
  const couponData = validatedParsed.data?.coupon || {};
  const discount = couponData.discount;
  const type = couponData.type == 'percentage' ? 'percent' : 'fixed';
  const couponId = couponData.id;

  // ── Étape 2 : Appliquer le coupon ────────────────────────────────────────
  const applyRequestId = `applyCoupon-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: applyRequestId,
    url: constants.ENDPOINTS.CART.APPLY_COUPON.replace(/{userId}/, userId),
    method: 'POST',
    data: { id: couponId, code: payload.code, discount, type }
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === applyRequestId) {
      const parsed = serializer.deserialize(res);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.COUPON.APPLIED, parsed.data);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'success',
          title: 'Coupon appliqué',
          body: 'Votre réduction a été appliquée au panier.'
        });
      } else {
        const errorMsg =
          parsed.error?.message ||
          parsed.message ||
          'Code promo invalide ou expiré';
        bus.emit(constants.EVENTS.CART.COUPON.APPLY_ERROR, {
          message: errorMsg
        });
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur',
          body: errorMsg
        });
      }
      finishLoading();
    }
  });
});

// ============ REMOVE COUPON ============
bus.on(constants.EVENTS.CART.COUPON.REMOVE, async () => {
  const requestId = `removeCoupon-${Date.now()}`;

  bus.emit(constants.EVENTS.CART.COUPON.REMOVING);
  await startLoadingSmoothly();

  const userId = serializer.getUserId();
  const url = `${constants.ENDPOINTS.CART.REMOVE_COUPON.replace(/{userId}/, userId)}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: url,
    method: 'POST',
    data: {}
  });

  on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = serializer.deserialize(res);

      if (parsed.success) {
        bus.emit(constants.EVENTS.CART.COUPON.REMOVED, parsed.data);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'success',
          title: 'Coupon retiré',
          body: 'Le coupon a été retiré de votre panier.'
        });
      } else {
        bus.emit(constants.EVENTS.CART.COUPON.REMOVE_ERROR, parsed);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur',
          body: 'Impossible de retirer le coupon.'
        });
      }
      finishLoading();
    }
  });
});

// ============ BRIDGE FOR LEGACY COMPONENTS ============
bus.on('cart_promo_code_apply', (payload) => {
  bus.emit(constants.EVENTS.CART.COUPON.APPLY, payload);
});

async function getRestaurant(restaurantId) {
  const restaurantRequestId = `fetchRestaurant-${Date.now()}`;
  const restaurantUrl = `${constants.ENDPOINTS.RESTAURANTS.DETAIL}`.replace(
    '{id}',
    restaurantId
  );

  console.log('[Cart Controller] Fetching restaurant:', restaurantUrl);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: restaurantRequestId,
    url: restaurantUrl,
    method: 'GET',
    data: {}
  });

  // Wait for restaurant response
  const restaurantPromise = new Promise((resolve, reject) => {
    const handler = (res) => {
      if (res.id === restaurantRequestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        if (res.success && res.data?.data) {
          resolve(restaurantSerializer.deserializeRestaurant(res.data.data));
        } else {
          reject(new Error('Failed to fetch restaurant'));
        }
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  });

  const restaurant = await restaurantPromise;
  return restaurant;
}
